import { useEffect, useRef } from 'react'
import { Autocomplete, Circle, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

// One loader config for the whole app: useJsApiLoader must use identical options everywhere
export const GOOGLE_MAPS_LIBRARIES = ['places']

const MAP_OPTIONS = { fullscreenControl: false, streetViewControl: false, mapTypeControl: false }
const SEARCH_ZOOM = 15

const GoogleCoverageMap = ({ apiKey, position, zoom, radiusEnabled, radiusKm, onPick }) => {
    const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES })
    const mapRef = useRef(null)
    const autocompleteRef = useRef(null)

    // Pan the map whenever position changes (search, click, current location, lat/lng input)
    useEffect(() => {
        if (!mapRef.current || !position) return
        mapRef.current.panTo({ lat: position.lat, lng: position.lng })
        if (zoom) mapRef.current.setZoom(zoom)
    }, [position, zoom])

    const handlePlaceChanged = () => {
        const location = autocompleteRef.current?.getPlace()?.geometry?.location
        if (!location) return
        onPick({ lat: location.lat(), lng: location.lng() })
        mapRef.current?.setZoom(SEARCH_ZOOM)
    }

    if (loadError) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ff4d4f' }}>Failed to load Google Maps</div>
    if (!isLoaded) return <Spin style={{ margin: 'auto', display: 'block', paddingTop: 140 }} />

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <div style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 1, maxWidth: 360 }}>
                <Autocomplete
                    onLoad={(instance) => { autocompleteRef.current = instance }}
                    onPlaceChanged={handlePlaceChanged}
                >
                    <input
                        type="text"
                        placeholder="Search a location"
                        style={{
                            width: '100%',
                            height: 36,
                            padding: '0 12px 0 34px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 8,
                            background: '#fff',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                            outline: 'none',
                            fontSize: 14,
                        }}
                    />
                </Autocomplete>
                <SearchOutlined style={{ position: 'absolute', top: 11, left: 12, color: '#8c8c8c', pointerEvents: 'none' }} />
            </div>
            <GoogleMap
                mapContainerStyle={{ height: '100%', width: '100%' }}
                center={{ lat: position.lat, lng: position.lng }}
                zoom={zoom}
                options={MAP_OPTIONS}
                onLoad={(map) => { mapRef.current = map }}
                onClick={(e) => onPick({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
            >
                <Marker
                    position={{ lat: position.lat, lng: position.lng }}
                    draggable
                    onDragEnd={(e) => onPick({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                />
                {radiusEnabled && (
                    <Circle
                        center={{ lat: position.lat, lng: position.lng }}
                        radius={(Number(radiusKm) || 10) * 1000}
                        options={{ strokeColor: '#1677ff', fillColor: '#1677ff', fillOpacity: 0.18, strokeWeight: 2 }}
                    />
                )}
            </GoogleMap>
        </div>
    )
}

export default GoogleCoverageMap
